import React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Image from 'components/image';
import Heading from 'components/heading';
import HousingProjectImageGalleryCarousel from 'components/housing-project-image-gallery-carousel';
import Modal from 'react-modal';
import ContentWrapper from 'components/content-wrapper';
import Icon from 'components/icon';

const themes = {
  dark: 'dark',
  light: 'light'
};

const HousingProjectHeaderWithCarousel = ({
  slides,
  image,
  logo1,
  logo2,
  logo3,
  logo4,
  title,
  theme = themes.light,
  initialSlide = 0,
  nextButtonText = 'Neste bilde',
  previousButtonText = 'Forrige bilde',
  openCarouselButtonText = 'Åpne bildegalleri',
  closeCarouselButtonText = 'Lukk bildegalleri'
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const hasGallery = Array.isArray(slides) && slides.length > 1;
  image = Array.isArray(slides) && slides.length > 0 ? slides[0].image : image;

  return (
    <div
      className={cn(
        'housing-project-header-with-carousel',
        `housing-project-header-with-carousel--${theme}`
      )}
    >
      <ContentWrapper className="housing-project-header-with-carousel__wrapper">
        {title && (
          <h1 className="housing-project-header-with-carousel__hidden-title">
            {title.projectName}
          </h1>
        )}
        <div className="housing-project-header-with-carousel__header">
          {title && (
            <Heading
              level={2}
              theme={Heading.themes.h2Hero}
              className="housing-project-header-with-carousel__title"
            >
              {title.text}
            </Heading>
          )}
          <div className="housing-project-header-with-carousel__logos">
            {logo1 && (
              <Image
                className="housing-project-header-with-carousel__logo"
                {...logo1}
                isLogo
              />
            )}
            {logo2 && (
              <Image
                className="housing-project-header-with-carousel__logo"
                {...logo2}
                isLogo
              />
            )}
            {logo3 && (
              <Image
                className="housing-project-header-with-carousel__logo"
                {...logo3}
                isLogo
              />
            )}
            {logo4 && (
              <Image
                className="housing-project-header-with-carousel__logo"
                {...logo4}
                isLogo
              />
            )}
          </div>
        </div>

        <div className="housing-project-header-with-carousel__carousel">
          {hasGallery ? (
            <HousingProjectImageGalleryCarousel
              slides={slides}
              initialId={initialSlide}
              nextButtonText={nextButtonText}
              previousButtonText={previousButtonText}
              onSlideChange={setCurrentSlide}
              onEnlarge={() => setModalOpen(true)}
              infinite
              embedded
              eagerLoad
            />
          ) : (
            image && (
              <div className="housing-project-header-with-carousel__image-fallback">
                <Image {...image} theme="stretch" />
              </div>
            )
          )}
        </div>
      </ContentWrapper>
      {hasGallery && (
        <Modal
          className="housing-project-header-with-carousel__modal"
          overlayClassName="housing-project-header-with-carousel__modal-overlay"
          isOpen={isModalOpen}
          onRequestClose={() => setModalOpen(false)}
          contentLabel={openCarouselButtonText}
        >
          <button
            aria-label={closeCarouselButtonText}
            className="housing-project-header-with-carousel__modal-close"
            onClick={() => setModalOpen(false)}
          >
            <Icon name="x" />
          </button>
          <HousingProjectImageGalleryCarousel
            slides={slides}
            initialId={currentSlide}
            nextButtonText={nextButtonText}
            previousButtonText={previousButtonText}
            infinite
            eagerLoad
          />
        </Modal>
      )}
    </div>
  );
};

HousingProjectHeaderWithCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.exact(Image.propTypes),
      description: PropTypes.string
    })
  ),
  image: PropTypes.exact(Image.propTypes),
  logo1: PropTypes.exact(Image.propTypes),
  logo2: PropTypes.exact(Image.propTypes),
  logo3: PropTypes.exact(Image.propTypes),
  logo4: PropTypes.exact(Image.propTypes),
  title: PropTypes.shape({
    text: PropTypes.string,
    projectName: PropTypes.string
  }),
  theme: PropTypes.oneOf(Object.keys(themes)),
  initialSlide: PropTypes.number,
  nextButtonText: PropTypes.string,
  previousButtonText: PropTypes.string,
  openCarouselButtonText: PropTypes.string,
  closeCarouselButtonText: PropTypes.string
};

HousingProjectHeaderWithCarousel.propTypesMeta = {
  initialSlide: 'exclude',
  theme: 'exclude'
};

export default HousingProjectHeaderWithCarousel;
